import { useNavigate, useParams } from 'react-router-dom';
import { useEffect } from 'react';
import { useLazyQuery } from '@apollo/client';
import { SpotifyGetReportDocument } from 'wb-spotify/graphql/generated';
import useSetCreateSpotifyReportData from 'wb-spotify/app/routes/reports/hooks/useSetCreateSpotifyReportData';
import useCreateSpotifyReport from 'wb-spotify/app/routes/reports/create/useCreateSpotifyReport';
import { useTranslation } from 'react-i18next';
import { captureException } from '@sentry/react';
import { handleErrorNotification } from '@aleph/shared-components';

/**
 * What this hook does is it fetches the report data when the report id is present in the url.
 * Then it sets the report data in the create report store which causes the single report component to rendered.
 */
const useSpotifyReportUrlOverlay = () => {
	const { params: editReportId } = useParams();
	const setEditingReportData = useSetCreateSpotifyReportData();
	const { cancelCreation } = useCreateSpotifyReport();
	const [fetchReportData] = useLazyQuery(SpotifyGetReportDocument, {
		// Disable cache so we get the latest report data
		fetchPolicy: 'network-only',
	});

	const { t } = useTranslation();
	const navigate = useNavigate();

	const setEditingReport = async (reportId?: string) => {
		if (!reportId) {
			cancelCreation();

			return;
		}

		const { data, error } = await fetchReportData({ variables: { reportId } });
		const report = data?.reports?.items?.[0];

		if (error || !report) {
			captureException(error);

			handleErrorNotification(
				error,
				t('re: something went wrong while fetching report', 'Something went wrong while fetching report'),
			);

			navigate('/reports');

			return;
		}

		setEditingReportData(report);
	};

	useEffect(() => {
		setEditingReport(editReportId);
	}, [editReportId]);
};

export default useSpotifyReportUrlOverlay;
